import React, { useState } from 'react'
import Head from 'next/head'
import Header from '../header'
import Footer from '../footer'
import styles from "../../styles/BmcHelix.module.css"
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next';
import { GetStaticProps, InferGetStaticPropsType } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { IMAGE_DELIVERY, imageLoader } from '@/base/utils/dataHelper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from "swiper";
import StoneityButton from '@/components/stoneity-button/stoneityButton';
import Image, { ImageLoaderProps } from 'next/image';
import "swiper/css";

enum DiscoveryUseCasesEnum {
  Security,
  Service,
  Enterprise,
  Cloud
}

type BmcHelixDiscoveryProps = InferGetStaticPropsType<typeof getStaticProps> & {
  setWidgetMenuOpen: (value: boolean) => void;
};

const BmcHelixDiscovery = ({ setWidgetMenuOpen }: BmcHelixDiscoveryProps) => {
  const { t } = useTranslation("common");
  const router = useRouter();
  const [productFeaturesTab, setProductFeaturesTab] = useState(DiscoveryUseCasesEnum.Security)

  const baseUrl = 'https://stoneity.com';
  const currentPath = router.asPath;
  const locale = router.locale || 'en';
  const canonicalUrl = locale === 'tr'
    ? `${baseUrl}/tr${currentPath}`
    : `${baseUrl}${currentPath}`;
  const alternateLocale = locale === 'tr' ? 'en' : 'tr';
  const alternatePath = '/bmc-helix-discovery';

  return (
    <>
      <Head>
        <title>{t('seo_bmc_helix_discovery_title')}</title>
        <meta name="description" content={t('seo_bmc_helix_discovery_description')} />
        <meta name="keywords" content="BMC Helix Discovery, IT asset discovery, CMDB, observability, multi-cloud visibility, Stoneity" />
        <link key="canonical" rel="canonical" href={canonicalUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={t('seo_bmc_helix_discovery_title')} />
        <meta property="og:description" content={t('seo_bmc_helix_discovery_description')} />
        <meta property="og:image" content={`${baseUrl}/stoneityLogo.png`} />
        <meta property="og:site_name" content="Stoneity" />
        <meta property="og:locale" content={locale === 'tr' ? 'tr_TR' : 'en_US'} />
        <meta property="og:locale:alternate" content={locale === 'tr' ? 'en_US' : 'tr_TR'} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={canonicalUrl} />
        <meta name="twitter:title" content={t('seo_bmc_helix_discovery_title')} />
        <meta name="twitter:description" content={t('seo_bmc_helix_discovery_description')} />
        <meta name="twitter:image" content={`${baseUrl}/stoneityLogo.png`} />
        <meta name="twitter:site" content="@stoneity" />

        <link rel="alternate" hrefLang={locale} href={canonicalUrl} />
        <link rel="alternate" hrefLang={alternateLocale} href={`${baseUrl}/${alternateLocale}${alternatePath}`} />
        <link rel="alternate" hrefLang="x-default" href={`${baseUrl}${alternatePath}`} />
      </Head>
      <Header />
      <main className={styles.mainFlex}>
        <div className={styles.pageFlex}>
          <div className={styles.discoveryBannerWrapper}>
            <div className={styles.discoveryBannerLeft}>
              <div className={styles.discoveryBannerTitle}>BMC Helix Discovery</div>
              <div className={styles.discoveryBannerExplain}>{t("unlockItVisibilityAndControl")}</div>
              <div className={styles.talkWithUsButtonWrapper}>
                <div className={styles.talkWithUsButtonImgWrapper}>
                  <img src="/human-1.png" alt="" />
                  <img src="/human-2.png" alt="" />
                  <img src="/human-3.png" alt="" />
                </div>
                <StoneityButton
                  buttonClass="talkWithUsButton"
                  buttonText={t("talkWithUs")}
                  buttonStyle="light"
                  width={200}
                  height={45}
                  onClick={() => setWidgetMenuOpen(true)}
                />
              </div>
            </div>
            <Image
              className={styles.discoveryBannerImg}
              src="/helixDiscoveryBanner.svg"
              alt=""
              width={600}
              height={600}
              priority
              sizes="(max-width: 1100px) 350px, (max-width: 1250px) 450px, 600px"
            />
          </div>
        </div>
        <div className={styles.pageFlex}>
          <div className={styles.discoveryOverviewWrapper}>
            <div className={styles.discoveryOverviewTop}>
              <div className={styles.discoveryOverviewTitle}>{t("driveOperationalExcellence")}</div>
              <div className={styles.discoveryOverviewExplain}>{t("availableDeploymentOptions")}</div>
            </div>
            <div className={styles.discoveryOverviewBottom}>
              <div className={styles.discoveryOverviewBottomRow}>
                <div className={styles.discoveryOverviewBottomRowTitle}>314%</div>
                <div className={styles.discoveryOverviewBottomRowExplain}>{t("roiIncreaseThreeYears")}</div>
              </div>
              <div className={styles.discoveryOverviewBottomRow}>
                <div className={styles.discoveryOverviewBottomRowTitle}>95%</div>
                <div className={styles.discoveryOverviewBottomRowExplain}>{t("improvementInAssetSecurity")}</div>
              </div>
              <div className={styles.discoveryOverviewBottomRow}>
                <div className={styles.discoveryOverviewBottomRowTitle}>{t("sevenMillionValue")}</div>
                <div className={styles.discoveryOverviewBottomRowExplain}>{t("totalImprovementValue")}</div>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.pageFlex}>
          <div className={styles.discoveryExplainRowWrapper}>
            <div className={styles.discoveryExplainRow}>
              <div className={styles.discoveryExplainRowLeft}>
                <div className={styles.discoveryExplainRowTitle}>{t("discoverAndVisualize")}</div>
                <div className={styles.discoveryExplainRowExplain}>{t("discoverAndVisualizeDescription")}</div>
                <ul className={styles.discorveryExplainRowItem}>
                  <li>
                    {t("agentlessContinuousDiscovery")}
                    <span className={styles.discorveryExplainRowItemDescription}>{t("agentlessContinuousDiscoveryDescription")}</span>
                  </li>
                  <li>
                    {t("blueprintAutomatedServiceModeling")}
                    <span className={styles.discorveryExplainRowItemDescription}>{t("blueprintAutomatedServiceModelingDescription")}</span>
                  </li>
                  <li>
                    {t("dataReconciliation")}
                    <span className={styles.discorveryExplainRowItemDescription}>{t("dataReconciliationDescription")}</span>
                  </li>
                </ul>
              </div>
              <Image
                className={styles.discoveryExplainRowImg}
                src={`${IMAGE_DELIVERY}/106e902e-81af-4472-9600-a77bd3f39d00`}
                alt=""
                width={2408}
                height={1636}
                loader={(p: ImageLoaderProps) => imageLoader(p.src, 2408, 1636)}
              />
            </div>
            <div className={styles.discoveryExplainRow}>
              <Image
                className={styles.discoveryExplainRowImg}
                src={`${IMAGE_DELIVERY}/6b13ac66-bbda-4cd8-4c25-3125abb3df00`}
                alt=""
                width={2404}
                height={1586}
                loader={(p: ImageLoaderProps) => imageLoader(p.src, 2404, 1586)}
              />
              <div className={styles.discoveryExplainRowLeft}>
                <div className={styles.discoveryExplainRowTitle}>{t("enableTrueObservability")}</div>
                <div className={styles.discoveryExplainRowExplain}>{t("enableTrueObservabilityDescription")}</div>
                <ul className={styles.discorveryExplainRowItem}>
                  <li>
                    {t("realTimeServiceAwareness")}
                    <span className={styles.discorveryExplainRowItemDescription}>{t("realTimeServiceAwarenessDescription")}</span>
                  </li>
                  <li>
                    {t("crucialContext")}
                    <span className={styles.discorveryExplainRowItemDescription}>{t("crucialContextDescription")}</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className={styles.discoveryExplainRow}>
              <div className={styles.discoveryExplainRowLeft}>
                <div className={styles.discoveryExplainRowTitle}>{t("enhanceSecurityCompliance")}</div>
                <div className={styles.discoveryExplainRowExplain}>{t("enhanceSecurityComplianceDescription")}</div>
                <ul className={styles.discorveryExplainRowItem}>
                  <li>
                    {t("regulatoryCompliance")}
                    <span className={styles.discorveryExplainRowItemDescription}>{t("regulatoryComplianceDescription")}</span>
                  </li>
                  <li>
                    {t("blindSpotDetection")}
                    <span className={styles.discorveryExplainRowItemDescription}>{t("blindSpotDetectionDescription")}</span>
                  </li>
                  <li>
                    {t("sslTlsCertificateManagement")}
                    <span className={styles.discorveryExplainRowItemDescription}>{t("sslTlsCertificateManagementDescription")}</span>
                  </li>
                </ul>
              </div>
              <Image
                className={styles.discoveryExplainRowImg}
                src={`${IMAGE_DELIVERY}/e42464d6-1850-4347-6192-c91a04b32500`}
                alt=""
                width={2404}
                height={1462}
                loader={(p: ImageLoaderProps) => imageLoader(p.src, 2404, 1462)}
              />
            </div>
          </div>
        </div>
        <div className={styles.pageFlex}>
          <div className={styles.discoveryUseCasesWrapper}>
            <div className={styles.discoveryUseCasesTitle}>{t("discoverHelixBusinessChallenges")}</div>
            <div className={styles.discoveryUseCasesTabWrapper}>
              <div className={`${styles.discoveryUseCasesTab} ${productFeaturesTab === DiscoveryUseCasesEnum.Security ? styles.discoveryUseCasesTabActive : ''}`} onClick={() => { setProductFeaturesTab(DiscoveryUseCasesEnum.Security) }}>{t("securityAndCompliance")}</div>
              <div className={`${styles.discoveryUseCasesTab} ${productFeaturesTab === DiscoveryUseCasesEnum.Service ? styles.discoveryUseCasesTabActive : ''}`} onClick={() => { setProductFeaturesTab(DiscoveryUseCasesEnum.Service) }}>{t("serviceAwareness")}</div>
              <div className={`${styles.discoveryUseCasesTab} ${productFeaturesTab === DiscoveryUseCasesEnum.Enterprise ? styles.discoveryUseCasesTabActive : ''}`} onClick={() => { setProductFeaturesTab(DiscoveryUseCasesEnum.Enterprise) }}>{t("enterpriseAssetManagement")}</div>
              <div className={`${styles.discoveryUseCasesTab} ${productFeaturesTab === DiscoveryUseCasesEnum.Cloud ? styles.discoveryUseCasesTabActive : ''}`} onClick={() => { setProductFeaturesTab(DiscoveryUseCasesEnum.Cloud) }}>{t("multiCloudVisibility")}</div>
            </div>
            {productFeaturesTab === DiscoveryUseCasesEnum.Security &&
              <div className={styles.discoveryUseCasesContent}>
                <div className={styles.discoveryUseCasesContentLeft}>
                  <div className={styles.discoveryUseCasesContentLeftTitle}>{t("securityAndCompliance")}</div>
                  <div className={styles.discoveryUseCasesContentLeftExplain}>{t("securityAndComplianceDescription")}</div>
                </div>
                <Image
                  className={styles.discoveryUseCasesContentImg}
                  src={`${IMAGE_DELIVERY}/6b0491cc-30eb-43f9-cfb5-20bea2015300`}
                  alt=""
                  width={608}
                  height={406}
                  loader={(p: ImageLoaderProps) => imageLoader(p.src, 608, 406)}
                />
              </div>
            }
            {productFeaturesTab === DiscoveryUseCasesEnum.Service &&
              <div className={styles.discoveryUseCasesContent}>
                <div className={styles.discoveryUseCasesContentLeft}>
                  <div className={styles.discoveryUseCasesContentLeftTitle}>{t("serviceAwareness")}</div>
                  <div className={styles.discoveryUseCasesContentLeftExplain}>{t("serviceAwarenessDescription")}</div>
                </div>
                <Image
                  className={styles.discoveryUseCasesContentImg}
                  src={`${IMAGE_DELIVERY}/3e2de156-2b56-4b55-1017-5cdb11305c00`}
                  alt=""
                  width={608}
                  height={406}
                  loader={(p: ImageLoaderProps) => imageLoader(p.src, 608, 406)}
                />
              </div>
            }
            {productFeaturesTab === DiscoveryUseCasesEnum.Enterprise &&
              <div className={styles.discoveryUseCasesContent}>
                <div className={styles.discoveryUseCasesContentLeft}>
                  <div className={styles.discoveryUseCasesContentLeftTitle}>{t("enterpriseAssetManagement")}</div>
                  <div className={styles.discoveryUseCasesContentLeftExplain}>{t("enterpriseAssetManagementDescription")}</div>
                </div>
                <Image
                  className={styles.discoveryUseCasesContentImg}
                  src={`${IMAGE_DELIVERY}/1b76cce1-9df0-4fee-f8d4-af76498c6e00`}
                  alt=""
                  width={608}
                  height={406}
                  loader={(p: ImageLoaderProps) => imageLoader(p.src, 608, 406)}
                />
              </div>
            }
            {productFeaturesTab === DiscoveryUseCasesEnum.Cloud &&
              <div className={styles.discoveryUseCasesContent}>
                <div className={styles.discoveryUseCasesContentLeft}>
                  <div className={styles.discoveryUseCasesContentLeftTitle}>{t("multiCloudVisibility")}</div>
                  <div className={styles.discoveryUseCasesContentLeftExplain}>{t("multiCloudVisibilityDescription")}</div>
                </div>
                <Image
                  className={styles.discoveryUseCasesContentImg}
                  src={`${IMAGE_DELIVERY}/06802403-d7e8-450f-e859-eaa089f2ea00`}
                  alt=""
                  width={608}
                  height={406}
                  loader={(p: ImageLoaderProps) => imageLoader(p.src, 608, 406)}
                />
              </div>
            }
          </div>
          <div className={styles.discoveryUseCasesResponsiveWrapper}>
            <Swiper
              slidesPerView={1}
              className={styles.discoveryUseCasesResponsiveMySwiper}
              modules={[Navigation]}
              navigation={{
                prevEl: "." + styles.discoveryUseCasesResponsiveSlideWrapper,
                nextEl: "." + styles.discoveryUseCasesResponsiveSlideWrapper2,
              }}
            >
              <SwiperSlide>
                <div className={styles.discoveryUseCasesContentResponsive}>
                  <div className={styles.discoveryUseCasesContentLeft}>
                    <div className={styles.discoveryUseCasesContentLeftTitle}>{t("securityAndCompliance")}</div>
                    <div className={styles.discoveryUseCasesContentLeftExplain}>{t("securityAndComplianceDescription")}</div>
                  </div>
                  <Image
                    className={styles.discoveryUseCasesContentImg}
                    src={`${IMAGE_DELIVERY}/6b0491cc-30eb-43f9-cfb5-20bea2015300`}
                    alt=""
                    width={608}
                    height={406}
                    loader={(p: ImageLoaderProps) => imageLoader(p.src, 608, 406)}
                  />
                </div>
              </SwiperSlide>
              <SwiperSlide>
                <div className={styles.discoveryUseCasesContentResponsive}>
                  <div className={styles.discoveryUseCasesContentLeft}>
                    <div className={styles.discoveryUseCasesContentLeftTitle}>{t("serviceAwareness")}</div>
                    <div className={styles.discoveryUseCasesContentLeftExplain}>{t("serviceAwarenessDescription")}</div>
                  </div>
                  <Image
                    className={styles.discoveryUseCasesContentImg}
                    src={`${IMAGE_DELIVERY}/3e2de156-2b56-4b55-1017-5cdb11305c00`}
                    alt=""
                    width={608}
                    height={406}
                    loader={(p: ImageLoaderProps) => imageLoader(p.src, 608, 406)}
                  />
                </div>
              </SwiperSlide>
              <SwiperSlide>
                <div className={styles.discoveryUseCasesContentResponsive}>
                  <div className={styles.discoveryUseCasesContentLeft}>
                    <div className={styles.discoveryUseCasesContentLeftTitle}>{t("enterpriseAssetManagement")}</div>
                    <div className={styles.discoveryUseCasesContentLeftExplain}>{t("enterpriseAssetManagementDescription")}</div>
                  </div>
                  <Image
                    className={styles.discoveryUseCasesContentImg}
                    src={`${IMAGE_DELIVERY}/1b76cce1-9df0-4fee-f8d4-af76498c6e00`}
                    alt=""
                    width={608}
                    height={406}
                    loader={(p: ImageLoaderProps) => imageLoader(p.src, 608, 406)}
                  />
                </div>
              </SwiperSlide>
              <SwiperSlide>
                <div className={styles.discoveryUseCasesContentResponsive}>
                  <div className={styles.discoveryUseCasesContentLeft}>
                    <div className={styles.discoveryUseCasesContentLeftTitle}>{t("multiCloudVisibility")}</div>
                    <div className={styles.discoveryUseCasesContentLeftExplain}>{t("multiCloudVisibilityDescription")}</div>
                  </div>
                  <Image
                    className={styles.discoveryUseCasesContentImg}
                    src={`${IMAGE_DELIVERY}/06802403-d7e8-450f-e859-eaa089f2ea00`}
                    alt=""
                    width={608}
                    height={406}
                    loader={(p: ImageLoaderProps) => imageLoader(p.src, 608, 406)}
                  />
                </div>
              </SwiperSlide>
            </Swiper>
            <div className={styles.discoveryUseCasesResponsiveSlideWrapper}>
              <div className={styles.discoveryUseCasesResponsiveCustomLeftArrow}></div>
            </div>
            <div className={styles.discoveryUseCasesResponsiveSlideWrapper2}>
              <div className={styles.discoveryUseCasesResponsiveCustomRightArrow}></div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  )
}

export const getStaticProps: GetStaticProps = async ({ locale }) => ({
  props: {
    ...(await serverSideTranslations(locale || "tr", ["common"])),
  },
});

export default BmcHelixDiscovery;
